import React from 'react';
import { Text, Divider } from 'wix-ui-tpa/cssVars';
import { TPA_EXPERIMENTS } from '@wix/pricing-plans-common/experiments';
import { getSetupFeeName, hasSetupFee, isFreePlan } from '@wix/pricing-plans-utils';
import { useExperiments, useTranslation } from '@wix/yoshi-flow-editor';
import { getOrderCoupon, getPricesCoupon, hasOrderCoupon } from '../../../../../utils/domainUtils';
import { isStartDateCustomizable } from '../../../../../utils/plan';
import { CouponInput } from '../CouponInput';
import { DateInput } from './DateInput';
import { classes, st } from './Small.st.css';
import { SummaryProps } from './types';
import { useOrderSummary } from './useOrderSummary';
import { usePresenter } from './usePresenter';

export const SmallSummary: React.FC<SummaryProps> = ({
  plan,
  prices = [],
  benefits,
  order,
  startDate,
  setStartDate,
  minStartDate,
  maxStartDate,
  isStartDateDisabled,
  updatePriceDetails,
  updatePriceDetailsError,
  couponInputMode,
  couponLoading,
  couponCode,
  removeCoupon,
  applyCouponError,
  hasCoupons,
}) => {
  const { t } = useTranslation();
  const presenter = usePresenter(plan, prices, benefits, order);
  const orderSummary = useOrderSummary(plan, prices, order);
  const { total, subtotal, taxAmount, couponAmount, planPrice, setupFee } = orderSummary;

  const isCouponsEnabled = hasCoupons && !isFreePlan(plan);
  const hasCouponOnOrder = order && hasOrderCoupon(order);
  const couponId = hasCouponOnOrder ? getOrderCoupon(order!)?.id : getPricesCoupon(prices)?.id;

  const { experiments } = useExperiments();
  const isSetupFeeEnabled = experiments.enabled(TPA_EXPERIMENTS.SETUP_FEE);

  const paymentDisclaimer = presenter.getPaymentDisclaimer(isSetupFeeEnabled);
  const nextPayment = presenter.getNextPayment();

  return (
    <div className={classes.box}>
      <div className={classes.section}>
        <Text className={classes.heading} tagName="h2">
          {t('payment.checkout-summary.heading')}
        </Text>
        {isStartDateCustomizable(plan) ? (
          <DateInput
            label={t('payment.mini-checkout-summary.start-date')}
            disabled={isStartDateDisabled}
            dataHook="small-start-date-input"
            value={startDate}
            max={maxStartDate}
            min={minStartDate}
            onChange={setStartDate}
          />
        ) : null}
      </div>
      {isSetupFeeEnabled ? (
        <div className={classes.section}>
          <div className={st(classes.entry, { noWrap: true })}>
            <Text className={classes.labelBold} data-hook="payment-checkout-summary-plan-name">
              {presenter.getPlanName()}
            </Text>
            <Text className={classes.text}>{planPrice}</Text>
          </div>
          <Text className={classes.textLight}>{presenter.getDurationFormatted()}</Text>
          {presenter.showFreeTrial() && <Text className={classes.textLight}>{presenter.getFreeTrialFormatted()}</Text>}
          {presenter.hasSessions() && <Text className={classes.textLight}>{presenter.getSessionFormatted()}</Text>}
        </div>
      ) : (
        <div className={classes.section}>
          <div className={classes.entry}>
            <Text className={classes.label}>{t('payment.checkout-summary.plan')}</Text>
            <Text className={classes.value} data-hook="payment-checkout-summary-plan-name">
              {presenter.getPlanName()}
            </Text>
          </div>
          <div className={classes.entry}>
            <Text className={classes.label}>{t('payment.checkout-summary.duration')}</Text>
            <Text className={classes.value} data-hook="payment-checkout-small-summary-duration">
              {presenter.getDuration()}
            </Text>
          </div>
          {presenter.hasSessions() ? (
            <div className={classes.entry}>
              <Text className={classes.label}>{t('payment.checkout-summary.sessions')}</Text>
              <Text className={classes.value} data-hook="payment-checkout-small-summary-sessions">
                {presenter.getSessionCount()}
              </Text>
            </div>
          ) : null}
          {presenter.showFreeTrial() ? (
            <div className={classes.entry} data-hook="payment-checkout-small-summary-plan-free-trial">
              <Text className={classes.label} data-hook="payment-checkout-small-summary-plan-free-trial-label">
                {t('payment.checkout-summary.trial')}
              </Text>
              <Text className={classes.value} data-hook="payment-checkout-small-summary-free-trial-days">
                {presenter.getFreeTrialDays()}
              </Text>
            </div>
          ) : null}
          {presenter.showFreeTrial() && presenter.isFreeTrialUnavailable() ? (
            <div className={classes.freeTrialDisclaimerEntry}>
              <Text className={classes.freeTrialDisclaimer}>
                {t('payment.checkout-summary.trial-unavailable-disclaimer')}
              </Text>
            </div>
          ) : null}
        </div>
      )}
      {isSetupFeeEnabled && hasSetupFee(plan) && (
        <div className={classes.section}>
          <div className={st(classes.entry, { noWrap: true })}>
            <Text className={classes.labelBold}>{getSetupFeeName(plan)}</Text>
            <Text className={classes.text}>{setupFee}</Text>
          </div>
        </div>
      )}
      {isCouponsEnabled ? (
        <>
          <Divider className={classes.divider} />
          <CouponInput
            disabled={hasCouponOnOrder || isStartDateDisabled}
            mode={hasCouponOnOrder ? 'coupon' : couponInputMode}
            error={updatePriceDetailsError ?? applyCouponError}
            value={hasCouponOnOrder ? getOrderCoupon(order!)!.code! : couponCode}
            onApplyClick={(code) => updatePriceDetails(plan.id!, code)}
            onRemoveClick={() => removeCoupon(plan.id!, couponId!)}
            loading={couponLoading}
          />
        </>
      ) : null}
      {((taxAmount || presenter.hasCoupon()) && subtotal) ||
      (presenter.hasCoupon() && subtotal) ||
      (taxAmount && subtotal) ? (
        <>
          <Divider className={classes.divider} />
          <div className={classes.section}>
            {(taxAmount || presenter.hasCoupon()) && subtotal ? (
              <div className={classes.entry} data-hook="payment-checkout-small-summary-subtotal-row">
                <Text
                  className={isSetupFeeEnabled ? classes.text : classes.label}
                  data-hook="payment-checkout-small-summary-subtotal-label"
                >
                  {t('payment.checkout-summary.subtotal')}
                </Text>
                <Text
                  className={isSetupFeeEnabled ? classes.text : classes.value}
                  data-hook="payment-checkout-small-summary-subtotal"
                >
                  {subtotal}
                </Text>
              </div>
            ) : null}
            {presenter.hasCoupon() && subtotal ? (
              <div className={classes.entry} data-hook="payment-checkout-small-summary-coupon-discount-row">
                <Text
                  className={isSetupFeeEnabled ? classes.text : classes.label}
                  data-hook="payment-checkout-small-summary-coupon-discount-label"
                >
                  {t('payment.checkout-summary.coupon-code')}
                </Text>
                <Text
                  className={isSetupFeeEnabled ? classes.text : classes.value}
                  data-hook="payment-checkout-small-summary-coupon-discount"
                >
                  {couponAmount}
                </Text>
              </div>
            ) : null}
            {taxAmount && subtotal ? (
              <div className={classes.entry} data-hook="payment-checkout-small-summary-tax-row">
                <Text
                  className={isSetupFeeEnabled ? classes.text : classes.label}
                  data-hook="payment-checkout-small-summary-tax-label"
                >
                  {presenter.getTaxName()} ({presenter.getTaxRate()} %)
                </Text>
                <Text
                  className={isSetupFeeEnabled ? classes.text : classes.value}
                  data-hook="payment-checkout-small-summary-tax"
                >
                  {taxAmount}
                </Text>
              </div>
            ) : null}
          </div>
        </>
      ) : null}
      <Divider className={classes.divider} />
      <div className={classes.section}>
        <div className={classes.entry}>
          <Text className={classes.labelLarge} data-hook="payment-checkout-small-summary-plan-total">
            {presenter.getTotalLabel(isSetupFeeEnabled)}
          </Text>
          <Text className={classes.valueLarge} data-hook="payment-checkout-small-summary-price">
            {total}
          </Text>
        </div>
        {isSetupFeeEnabled && nextPayment && (
          <div className={classes.entry}>
            <Text className={classes.text}>{nextPayment.label}</Text>
            <Text className={classes.text}>{nextPayment.amount}</Text>
          </div>
        )}
        {presenter.showPaymentPeriod(isSetupFeeEnabled) ? (
          <div className={classes.paymentPeriodEntry}>
            <Text className={classes.paymentPeriod}>{presenter.getPaymentPeriod()}</Text>
          </div>
        ) : null}
      </div>
      {paymentDisclaimer ? (
        <div className={classes.section}>
          <div className={classes.entry}>
            <Text className={isSetupFeeEnabled ? classes.text : classes.value}>{paymentDisclaimer}</Text>
          </div>
        </div>
      ) : null}
    </div>
  );
};
